import {
  Box,
  Button,
  Flex,
  Form,
  SortableTable,
  SubmitButton,
  type TableColumnsType,
} from '@/components';
import { useChangeOrderOfMinistry } from '@/features/master-table/hooks';
import type {
  MinistriesModel,
  MinistryModel,
} from '@/features/master-table/models';
import { useSortOrder } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  dataSource: z.infer<typeof MinistriesModel>;
  columns: TableColumnsType<z.infer<typeof MinistryModel>>;
  onCancel: () => void;
  onSubmitSuccess: () => void;
};

export const MinistrySortableTable: FC<Props> = ({
  dataSource,
  columns,
  onCancel,
  onSubmitSuccess,
}) => {
  const { sortedData, onDragEnd, hasDataIndexChanged } =
    useSortOrder<z.infer<typeof MinistryModel>>(dataSource);
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { mutateAsync: updateSortOrderAsync } = useChangeOrderOfMinistry();

  const onSubmit = async () => {
    const params = sortedData.map((d, i) => ({
      id: d.id,
      sortOrder: i + 1,
    }));

    await updateSortOrderAsync(params);
    await Promise.all([
      openMessage('success', t('message-update-completed')),
      (async () => {
        onSubmitSuccess();
      })(),
    ]);
  };

  return (
    <Form
      leavePageAlertCondition={{
        additionalAlertCondition: hasDataIndexChanged,
      }}
      onFinish={onSubmit}
    >
      <Box marginBottom={12}>
        <Flex justify="flex-end" gap={4}>
          <Button onClick={onCancel}>{t('cancel')}</Button>
          <SubmitButton text={t('save')} disabled={!hasDataIndexChanged} />
        </Flex>
      </Box>

      <SortableTable<z.infer<typeof MinistriesModel>>
        dataSource={sortedData}
        columns={columns}
        rowKey="id"
        rowKeys={sortedData.map((d) => d.id)}
        onDragEnd={onDragEnd}
        pagination={false}
      />
    </Form>
  );
};
